import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  Alert,
  TextField,
  Button,
  Snackbar
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

interface Operator {
  id: number;
  name: string;
  code: string;
}

interface AdminPayoutMatrixCell {
  betType: string;
  multiplier?: number;
  isValid: boolean;
  isEditable: boolean;
  gameId?: number;
}

interface AdminPayoutMatrixRow {
  prizeTier: string;
  displayName: string;
  cells: AdminPayoutMatrixCell[];
}

interface AdminPayoutMatrixData {
  operatorId: number;
  operatorName: string;
  gameName: string;
  availableOperators: Operator[];
  rows: AdminPayoutMatrixRow[];
}

interface PayoutMatrixUpdate {
  prizeTier: string;
  betType: string;
  multiplier?: number;
  gameId?: number;
}

interface UpdatePayoutMatrixRequest {
  operatorId: number;
  updates: PayoutMatrixUpdate[];
}

const AdminPayoutMatrix: React.FC = () => {
  const [selectedOperatorId, setSelectedOperatorId] = useState<number | null>(null);
  const [editedValues, setEditedValues] = useState<Record<string, number | null>>({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const queryClient = useQueryClient();

  const { data: payoutMatrix, isLoading, error } = useQuery<AdminPayoutMatrixData>({
    queryKey: ['admin-payout-matrix', selectedOperatorId],
    queryFn: async () => {
      const params = selectedOperatorId ? `?operatorId=${selectedOperatorId}` : '';
      const response = await axios.get(`/admin/payout-matrix${params}`);
      return response.data;
    },
    retry: 2,
    staleTime: 0, // No caching - always fetch fresh data
  });

  const updateMutation = useMutation({
    mutationFn: async (request: UpdatePayoutMatrixRequest) => {
      const response = await axios.put('/admin/payout-matrix', request);
      return response.data;
    },
    onSuccess: () => {
      setShowSuccess(true);
      setEditedValues({});
      queryClient.invalidateQueries({ queryKey: ['admin-payout-matrix', selectedOperatorId] });
    },
    onError: (error: any) => {
      setErrorMessage(error.response?.data?.message || 'Failed to update payout matrix');
      setShowError(true);
    }
  });

  const handleOperatorChange = (operatorId: number) => {
    setSelectedOperatorId(operatorId);
    setEditedValues({});
  };

  const handleCellChange = (prizeTier: string, betType: string, value: string) => {
    const key = `${prizeTier}-${betType}`;
    const numValue = value === '' ? null : parseFloat(value);
    setEditedValues(prev => ({
      ...prev,
      [key]: numValue
    }));
  };

  const handleSave = () => {
    if (!payoutMatrix) return;

    const updates: PayoutMatrixUpdate[] = [];

    // Collect all changes
    Object.entries(editedValues).forEach(([key, value]) => {
      const [prizeTier, betType] = key.split('-');
      
      // Find the original cell to get gameId
      const row = payoutMatrix.rows.find(r => r.prizeTier === prizeTier);
      const cell = row?.cells.find(c => c.betType === betType);
      
      if (cell && cell.isEditable && cell.gameId) {
        updates.push({
          prizeTier,
          betType,
          multiplier: value ?? undefined,
          gameId: cell.gameId
        });
      }
    });

    if (updates.length === 0) {
      setErrorMessage('No changes to save');
      setShowError(true);
      return;
    }

    const request: UpdatePayoutMatrixRequest = {
      operatorId: payoutMatrix.operatorId,
      updates
    };

    updateMutation.mutate(request);
  };

  const hasChanges = Object.keys(editedValues).length > 0;

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">
            Failed to load admin payout matrix. Please try again later.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!payoutMatrix) {
    return (
      <Card>
        <CardContent>
          <Alert severity="info">
            No admin payout matrix data available.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const betTypes = ['Big', 'Small', '4A', 'A', 'ABC', '5D', '6D'];

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h6">
            Admin - {payoutMatrix.operatorName} - {payoutMatrix.gameName}
          </Typography>
          
          <Box display="flex" gap={2} alignItems="center">
            <Box display="flex" gap={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ mr: 1 }}>
                Select Operator:
              </Typography>
              {payoutMatrix.availableOperators.map((operator) => (
                <Button
                  key={operator.id}
                  variant={selectedOperatorId === operator.id || (!selectedOperatorId && operator.id === payoutMatrix.operatorId) ? "contained" : "outlined"}
                  size="small"
                  onClick={() => handleOperatorChange(operator.id)}
                  sx={{
                    borderRadius: '8px',
                    fontWeight: 'bold',
                    px: 2,
                    py: 1,
                    whiteSpace: 'nowrap'
                  }}
                >
                  {operator.name}
                </Button>
              ))}
            </Box>

            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={!hasChanges || updateMutation.isPending}
              sx={{ minWidth: 100 }}
            >
              {updateMutation.isPending ? <CircularProgress size={20} /> : 'Save Changes'}
            </Button>
          </Box>
        </Box>

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#1976d2', color: 'white' }}>
                  Details
                </TableCell>
                {betTypes.map((betType) => (
                  <TableCell 
                    key={betType}
                    sx={{ fontWeight: 'bold', backgroundColor: '#1976d2', color: 'white', textAlign: 'center' }}
                  >
                    {betType}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {payoutMatrix.rows.map((row) => (
                <TableRow key={row.prizeTier}>
                  <TableCell sx={{ fontWeight: 'medium' }}>
                    {row.displayName}
                  </TableCell>
                  {row.cells.map((cell) => {
                    const key = `${row.prizeTier}-${cell.betType}`;
                    const editedValue = editedValues[key];
                    const displayValue = editedValue !== undefined ? editedValue : cell.multiplier;
                    
                    return (
                      <TableCell 
                        key={cell.betType}
                        sx={{ 
                          textAlign: 'center',
                          backgroundColor: cell.isValid ? 'white' : '#f5f5f5',
                          padding: '4px'
                        }}
                      >
                        {cell.isEditable ? (
                          <TextField
                            size="small"
                            variant="outlined"
                            value={displayValue?.toString() || ''}
                            onChange={(e) => handleCellChange(row.prizeTier, cell.betType, e.target.value)}
                            inputProps={{
                              style: { textAlign: 'center', fontSize: '0.875rem' }
                            }}
                            sx={{
                              width: '80px',
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                  borderColor: editedValue !== undefined ? '#1976d2' : undefined,
                                },
                              },
                            }}
                          />
                        ) : (
                          <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                            -
                          </Typography>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box mt={2}>
          <Typography variant="caption" color="text.secondary">
            Admin view - Edit default payout multipliers for {payoutMatrix.operatorName}.
            {hasChanges && (
              <Typography component="span" variant="caption" color="primary" sx={{ ml: 1 }}>
                • You have unsaved changes
              </Typography>
            )}
          </Typography>
        </Box>

        <Snackbar
          open={showSuccess}
          autoHideDuration={3000}
          onClose={() => setShowSuccess(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={() => setShowSuccess(false)} severity="success">
            Payout matrix updated successfully!
          </Alert>
        </Snackbar>

        <Snackbar
          open={showError}
          autoHideDuration={5000}
          onClose={() => setShowError(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={() => setShowError(false)} severity="error">
            {errorMessage}
          </Alert>
        </Snackbar>
      </CardContent>
    </Card>
  );
};

export default AdminPayoutMatrix;
